import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Send, Instagram, Facebook } from "lucide-react";
import { CONTACT_REASONS } from "../data/content";
import { PageHero } from "../components/shared/PageHero";
import { Reveal } from "../components/shared/Reveal";

const inputClass =
  "w-full border border-white/10 bg-ink-soft px-5 py-4 text-sm text-bone placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-kred";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-3 block font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">
      {label}
    </label>
    {children}
  </div>
);

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const emptyForm = { name: "", organisation: "", email: "", phone: "", reason: CONTACT_REASONS[0], message: "", website: "" };
  const [form, setForm] = useState(emptyForm);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError(false);
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      setSent(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Talk To Us"
        intro="Federations, athletes, sponsors, media and community — every conversation about Kumite Games starts here."
        testId="contact-page-hero"
      />
      <section className="mx-auto max-w-3xl px-5 pb-24 pt-8 sm:px-8 sm:pb-32">
        <Reveal className="mb-8 flex items-center justify-center gap-3 border border-white/5 bg-ink-soft px-6 py-5">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-kred">Email</span>
          <a
            href="mailto:kumitegames.nz@gmail.com"
            data-testid="contact-email-link"
            className="font-mono text-sm tracking-wide text-bone transition-colors hover:text-gold"
          >
            kumitegames.nz@gmail.com
          </a>
        </Reveal>
        {sent ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="contact-success-message"
            className="clip-notch border border-gold/30 bg-gold/[0.05] p-12 text-center sm:p-16"
          >
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold" />
            <h2 className="mt-8 font-display text-4xl uppercase tracking-wide text-bone sm:text-5xl">
              Message Received
            </h2>
            <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
              Thank you for contacting Kumite Games. Your message has been received and our team
              will get back to you as soon as possible.
            </p>
            <button
              data-testid="contact-send-another-button"
              onClick={() => { setSent(false); setForm(emptyForm); }}
              className="mt-10 border border-white/15 px-8 py-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-300 transition-colors duration-300 hover:border-kred hover:text-kred"
            >
              Send Another Message
            </button>
          </motion.div>
        ) : (
          <Reveal>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-7 border border-white/5 bg-ink-card p-7 sm:p-12">
              <input
                type="text"
                name="website"
                data-testid="contact-honeypot-field"
                value={form.website}
                onChange={update("website")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="pointer-events-none absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0"
              />
              <div className="grid gap-7 sm:grid-cols-2">
                <Field label="Full Name *" htmlFor="contact-name">
                  <input id="contact-name" data-testid="contact-name-input" required maxLength={120} value={form.name} onChange={update("name")} placeholder="Your full name" className={inputClass} />
                </Field>
                <Field label="Organisation / Federation / Academy / Company" htmlFor="contact-organisation">
                  <input id="contact-organisation" data-testid="contact-organisation-input" maxLength={200} value={form.organisation} onChange={update("organisation")} placeholder="Club, federation or company" className={inputClass} />
                </Field>
              </div>
              <div className="grid gap-7 sm:grid-cols-2">
                <Field label="Email Address *" htmlFor="contact-email">
                  <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputClass} />
                </Field>
                <Field label="Phone Number" htmlFor="contact-phone">
                  <input id="contact-phone" data-testid="contact-phone-input" type="tel" maxLength={40} value={form.phone} onChange={update("phone")} placeholder="+64 ..." className={inputClass} />
                </Field>
              </div>
              <Field label="Reason for Contact *" htmlFor="contact-reason">
                <select id="contact-reason" data-testid="contact-reason-select" required value={form.reason} onChange={update("reason")} className={`${inputClass} appearance-none`}>
                  {CONTACT_REASONS.map((r) => (
                    <option key={r} value={r} className="bg-ink-soft">{r}</option>
                  ))}
                </select>
              </Field>
              <Field label="Message *" htmlFor="contact-message">
                <textarea id="contact-message" data-testid="contact-message-input" required rows={6} maxLength={3000} value={form.message} onChange={update("message")} placeholder="How can we help?" className={`${inputClass} resize-y`} />
              </Field>
              {error && (
                <p data-testid="contact-error-message" className="border border-kred/40 bg-kred/10 px-5 py-4 text-sm leading-relaxed text-red-300">
                  We couldn&apos;t send your message. Please try again or contact us directly at{" "}
                  <a href="mailto:kumitegames.nz@gmail.com" className="underline">kumitegames.nz@gmail.com</a>.
                </p>
              )}
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="clip-notch-sm group inline-flex w-full items-center justify-center gap-3 bg-kred px-8 py-5 font-mono text-xs font-bold uppercase tracking-[0.3em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-red-500 hover:shadow-[0_12px_40px_-8px_rgba(220,38,38,0.5)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none sm:w-auto"
              >
                {sending ? "Sending…" : "Send Message"}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        )}
        <Reveal className="mt-12">
          <div data-testid="follow-section" className="border border-white/5 bg-ink-card p-8 text-center sm:p-10">
            <h2 className="font-mono text-xs font-bold uppercase tracking-[0.35em] text-kred">
              Follow Kumite Games
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <a
                href="https://www.instagram.com/kumitegames/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Kumite Games on Instagram"
                data-testid="contact-social-instagram"
                className="group border border-white/10 px-6 py-6 transition-colors duration-300 hover:border-gold/50"
              >
                <Instagram className="mx-auto h-5 w-5 text-slate-400 transition-colors duration-300 group-hover:text-gold" />
                <p className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">
                  Instagram
                </p>
                <p className="mt-1 font-head text-lg font-bold text-bone">@kumitegames</p>
              </a>
              <a
                href="https://www.facebook.com/kumitegamesnz/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Kumite Games on Facebook"
                data-testid="contact-social-facebook"
                className="group border border-white/10 px-6 py-6 transition-colors duration-300 hover:border-gold/50"
              >
                <Facebook className="mx-auto h-5 w-5 text-slate-400 transition-colors duration-300 group-hover:text-gold" />
                <p className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">
                  Facebook
                </p>
                <p className="mt-1 font-head text-lg font-bold text-bone">@kumitegames</p>
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
